import React from "react";
import { Loader2 } from "lucide-react";
import { CaseManagerSelect } from "../CaseManagerSelect";
import { STATUSES, statusStyle } from "./leadStatus";

const formatReceived = (createdAt) =>
  new Date(createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const LeadRow = ({ lead, caseManagers, isAdmin, onSelect, onStatusChange, onCaseManagerChange, onCaseManagersOptionsChange }) => (
  <tr
    onClick={() => onSelect(lead.id)}
    className="border-t border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors cursor-pointer"
    data-testid={`lead-row-${lead.id}`}
  >
    <td className="px-5 py-4">
      <div className="font-semibold text-[#111827]">{lead.full_name}</div>
      <div className="text-[#6B7280]">
        {lead.business_name}
        {lead.company_number && <span className="ml-1.5 text-[10px] font-bold text-[#052F24] bg-[#DFFF00]/60 rounded px-1 py-0.5">CH {lead.company_number}</span>}
      </div>
    </td>
    <td className="px-5 py-4">
      <div className="text-[#111827]">{lead.email}</div>
      <div className="text-[#6B7280]">{lead.phone}</div>
    </td>
    <td className="px-5 py-4 font-display font-extrabold text-lg">{lead.loan_amount}</td>
    <td className="px-5 py-4 text-[#6B7280] max-w-[200px]">
      {[lead.loan_purpose, lead.trading_time, lead.annual_turnover && `TO ${lead.annual_turnover}`].filter(Boolean).join(" · ")}
    </td>
    <td className="px-5 py-4" data-testid={`lead-homeowner-${lead.id}`}>
      {lead.homeowner ? (
        <span className={`inline-block whitespace-nowrap text-[10px] font-bold rounded-full px-2.5 py-1 ${lead.homeowner === "Yes" ? "bg-[#052F24]/10 text-[#052F24]" : "bg-[#F3F4F6] text-[#6B7280]"}`}>
          {lead.homeowner}
        </span>
      ) : (
        <span className="text-[#9CA3AF]">—</span>
      )}
    </td>
    {isAdmin && (
      <td className="px-5 py-4 max-w-[140px]" data-testid={`lead-reference-${lead.id}`}>
        {lead.referred_by ? (
          <span className="inline-block max-w-full truncate whitespace-nowrap text-[10px] font-bold text-[#052F24] bg-[#F3F4F6] rounded-full px-2.5 py-1">{lead.referred_by}</span>
        ) : (
          <span className="text-[#9CA3AF]">—</span>
        )}
      </td>
    )}
    {isAdmin && (
      <td className="px-5 py-4 max-w-[140px]" data-testid={`lead-packager-${lead.id}`}>
        {lead.packager ? (
          <span className="inline-block max-w-full truncate whitespace-nowrap text-[10px] font-bold text-[#052F24] bg-[#DFFF00]/50 rounded-full px-2.5 py-1">{lead.packager}</span>
        ) : (
          <span className="text-[#9CA3AF]">—</span>
        )}
      </td>
    )}
    <td className="px-5 py-4 text-[#6B7280] whitespace-nowrap">{formatReceived(lead.created_at)}</td>
    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
      <select
        value={lead.status}
        onChange={(e) => onStatusChange(lead.id, e.target.value)}
        data-testid={`status-select-${lead.id}`}
        className={`rounded-full px-3 py-1.5 text-xs font-bold border-0 outline-none cursor-pointer ${statusStyle[lead.status] || statusStyle.New}`}
      >
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </td>
    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
      <CaseManagerSelect
        value={lead.case_manager}
        options={caseManagers}
        onChange={(name) => onCaseManagerChange(lead.id, name)}
        onOptionsChange={onCaseManagersOptionsChange}
        testId={`case-manager-select-${lead.id}`}
      />
    </td>
  </tr>
);

export const LeadsTable = ({ leads, filtered, caseManagers, viewerRole = "admin", onSelect, onStatusChange, onCaseManagerChange, onCaseManagersOptionsChange }) => {
  const isAdmin = viewerRole === "admin";

  if (filtered === null) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-[#052F24]/40" /></div>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        <div className="p-16 text-center text-[#6B7280]" data-testid="empty-state">
          {leads.length === 0 ? "No leads yet. They'll appear here the moment someone applies." : "No leads match your search."}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead>
            <tr className="bg-[#F9FAFB] text-left text-[11px] font-bold tracking-[0.12em] uppercase text-[#6B7280]">
              <th className="px-5 py-4">Name / Business</th>
              <th className="px-5 py-4">Contact</th>
              <th className="px-5 py-4">Amount</th>
              <th className="px-5 py-4">Details</th>
              <th className="px-5 py-4">Homeowner</th>
              {isAdmin && <th className="px-5 py-4">Reference</th>}
              {isAdmin && <th className="px-5 py-4">Packager</th>}
              <th className="px-5 py-4">Received</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Case Manager</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                caseManagers={caseManagers}
                isAdmin={isAdmin}
                onSelect={onSelect}
                onStatusChange={onStatusChange}
                onCaseManagerChange={onCaseManagerChange}
                onCaseManagersOptionsChange={onCaseManagersOptionsChange}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
